import FW from '../fw/fw';
import Logger from '../fw/logger';
import CCManagement from '../accessibility/cc';
import StreamingHelpers from './streaming-helpers';
import UIManagement from '../ui/ui';
import Env from '../fw/env';


export default class NativeHls {

  #rmp;
  #nativeHlsResizeLevelSwitchFn = null;
  #nativeHlsSetUpAudioTracksFn = null;
  #nativeHlsSetUpCCTracksFn = null;
  #hlsTextTracksCheckingIntervalFn = null;
  #nativeHlsMetaExtXProgramDatetimeIntervalFn = null;
  #nativeHlsSetUpQualityFn = null;
  #previousLivePresentationStartTimeAsDate = -1;

  constructor(rmp) {
    this.#rmp = rmp;
    this.#rmp.rmpStreamingHelpers = new StreamingHelpers(rmp);
  }

  #reset() {
    this.#nativeHlsResizeLevelSwitchFn = null;
    this.#nativeHlsSetUpAudioTracksFn = null;
    this.#nativeHlsSetUpCCTracksFn = null;
    this.#hlsTextTracksCheckingIntervalFn = null;
    this.#nativeHlsMetaExtXProgramDatetimeIntervalFn = null;
    this.#nativeHlsSetUpQualityFn = null;
    this.#previousLivePresentationStartTimeAsDate = -1;
  }

  #nativeHlsResizeLevelSwitch() {
    if (this.#rmp.dom.video.videoHeight) {
      Logger.print('native-hls',
        'native HLS level switched - new resolution height is ' + this.#rmp.dom.video.videoHeight);
      this.#rmp.rmpUtils.fireLevelSwitchEvents();
    }
  }

  #nativeHlsSetUpAudioTracks() {
    if (this.#rmp.dom.video.audioTracks && this.#rmp.dom.video.audioTracks.length > 1) {
      Logger.print('native-hls', 'multi-audio tracks detected', this.#rmp.dom.video.audioTracks);
      this.#rmp.rmpModuleManagement.appendToControlBar('audio');
      this.#rmp.rmpModuleManagement.appendOverlay('audio');
      for (let i = 0; i < this.#rmp.dom.video.audioTracks.length; i++) {
        const currentAudioTrack = this.#rmp.dom.video.audioTracks[i];
        let className;
        if (currentAudioTrack.enabled) {
          className = 'rmp-button rmp-overlay-level rmp-color-bg rmp-overlay-level-active';
        } else {
          className = 'rmp-button rmp-overlay-level rmp-color-bg';
        }
        let textContent = '';
        if (this.#rmp.customTrackNames.audio.length > 0 && this.#rmp.customTrackNames.audio[i]) {
          textContent = this.#rmp.customTrackNames.audio[i];
        } else if (typeof currentAudioTrack.label === 'string' && currentAudioTrack.label !== '') {
          textContent = currentAudioTrack.label;
        } else if (typeof currentAudioTrack.language === 'string' && currentAudioTrack.language !== '') {
          textContent = currentAudioTrack.language;
        } else {
          textContent = 'Audio ' + i;
        }
        const langDiv = FW.createElement(
          'button',
          className,
          textContent,
          this.#rmp.rtl,
          true
        );
        this.#rmp.rmpAccessible.button(langDiv, textContent, true);
        const selectAudioTrackFn = this.#rmp.rmpStreamingHelpers.selectAudioTrack.bind(this.#rmp.rmpStreamingHelpers, i);
        FW.addEvents(['touchend', 'click'], langDiv, selectAudioTrackFn);
        this.#rmp.dom.audioOverlayLevelsArea.appendChild(langDiv);
      }
    }
    // set meta event for EXT-X-PROGRAM-DATETIME
    this.#nativeHlsMetaExtXProgramDatetimeIntervalFn = window.setInterval(() => {
      const livePresentationStartTimeAsDate = this.#rmp.livePresentationStartTimeAsDate;
      if (livePresentationStartTimeAsDate > -1) {
        const epochTime = new Date(livePresentationStartTimeAsDate).getTime();
        if (epochTime !== this.#previousLivePresentationStartTimeAsDate ||
          this.#previousLivePresentationStartTimeAsDate === -1) {
          this.#rmp.meta = {};
          this.#rmp.meta.type = 'program-date-time';
          this.#rmp.meta.data = {};
          this.#previousLivePresentationStartTimeAsDate = epochTime;
          this.#rmp.meta.data.programDateTime = epochTime;
          this.#rmp.meta.data.rawProgramDateTime = livePresentationStartTimeAsDate;
          this.#rmp.dispatch('meta');
          Logger.print(null, null, this.#rmp.meta);
        }
      }
    }, 1000);
  }

  #hlsTextTracksCheckingInterval() {
    if (this.#rmp.dom.video.textTracks.length > 0) {
      Logger.print('captions', 'following video.textTracks are now available', this.#rmp.dom.video.textTracks);
      window.clearInterval(this.#hlsTextTracksCheckingIntervalFn);
      this.#rmp.rmpCCManagement = new CCManagement(this.#rmp);
      this.#rmp.rmpCCManagement.readingCaptionsFromHls = true;
      this.#rmp.textTracks = StreamingHelpers.filterNativeTextTracks(this.#rmp.dom.video.textTracks);
      Logger.print('captions', 'filtered textTracks follows', this.#rmp.textTracks);
      if (this.#rmp.textTracks.length > 0) {
        this.#rmp.textTracks.forEach((textTrack, index) => {
          this.#rmp.loadedCCFiles[index] = {};
          let lng = textTrack.language || textTrack.lang;
          if (!lng) {
            lng = this.#rmp.labels.hint.track + ' ' + (index + 1);
          }
          this.#rmp.loadedCCFiles[index].lng = lng;
          let label = textTrack.label || textTrack.name;
          if (!label) {
            label = lng;
          }
          this.#rmp.loadedCCFiles[index].name = label;
          this.#rmp.loadedCCFiles[index].uri = textTrack.src || '';
          this.#rmp.loadedCCFiles[index].data = null;
          // update UI for HLS DEFAULT=YES attribute
          if (textTrack.mode && textTrack.mode === 'showing') {
            this.#rmp.loadedCCFiles[index].default = true;
          }
        });
        this.#rmp.rmpCCManagement.appendUI();
      }
    }
  }

  #nativeHlsSetUpCCTracks() {
    if (this.#rmp.dom.video.textTracks.length > 0) {
      Logger.print('captions', 'native HLS textTracks found', this.#rmp.dom.video.textTracks);
      this.#hlsTextTracksCheckingInterval();
    } else {
      this.#hlsTextTracksCheckingIntervalFn = window.setInterval(this.#hlsTextTracksCheckingInterval.bind(this), 400);
    }
  }

  #nativeHlsSetUpQuality() {
    this.#rmp.rmpModuleManagement.appendToControlBar('quality');
  }

  destroy() {
    window.clearInterval(this.#hlsTextTracksCheckingIntervalFn);
    window.clearInterval(this.#nativeHlsMetaExtXProgramDatetimeIntervalFn);
    this.#rmp.dom.video.removeEventListener('resize', this.#nativeHlsResizeLevelSwitchFn);
    this.#rmp.dom.video.removeEventListener('playing', this.#nativeHlsSetUpQualityFn);
    this.#rmp.dom.video.removeEventListener('playing', this.#nativeHlsSetUpAudioTracksFn);
    this.#rmp.dom.video.removeEventListener('playing', this.#nativeHlsSetUpCCTracksFn);
    this.#reset();
  }

  init() {
    this.#nativeHlsResizeLevelSwitchFn = this.#nativeHlsResizeLevelSwitch.bind(this);
    this.#rmp.dom.video.addEventListener('resize', this.#nativeHlsResizeLevelSwitchFn);

    // adds for native HLS an indicator of quality
    this.#nativeHlsSetUpQualityFn = this.#nativeHlsSetUpQuality.bind(this);
    this.#rmp.dom.video.addEventListener('playing', this.#nativeHlsSetUpQualityFn, { once: true });

    if (!this.#rmp.audioOnly) {
      // set up audio tracks
      this.#nativeHlsSetUpAudioTracksFn = this.#nativeHlsSetUpAudioTracks.bind(this);
      this.#rmp.dom.video.addEventListener('playing', this.#nativeHlsSetUpAudioTracksFn, { once: true });

      // set up text tracks
      if (!this.#rmp.hasExternalCCFiles) {
        this.#nativeHlsSetUpCCTracksFn = this.#nativeHlsSetUpCCTracks.bind(this);
        this.#rmp.dom.video.addEventListener('playing', this.#nativeHlsSetUpCCTracksFn, { once: true });
        if (this.#rmp.autoBumpCaptionsOnVisibleControlBar && !Env.isIos[0]) {
          UIManagement.appendCSSForNativeCCRendering();
        }
      }
    }
  }

}
